import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    TextField,
    Text,
    Stack,
    StackItem,
    ITextFieldStyles,
    TooltipHost,
    IRenderFunction,
    ITextFieldProps,
    Image,
    Modal,
    IModalStyles,
} from '@fluentui/react';
import queryString from "query-string";
import ButtonPrimaryComponent from '../../components/button-primary/button-primary.component';
import ErrorMessageComponent from '../../components/error-message/error-message.component';
import LoaderComponent from '../../components/loader/loader.component';
import SuccessMessageComponent from '../../components/success-message/success-message.component';
import TermsAndConditionsComponent from '../../components/terms-and-conditions/terms-and-conditions.component';
import PrivacyPolicyComponent from '../../components/privacy-policy/privacy-policy.component';

import useAuthStatus from '../../hooks/auth/AuthHook';
import ApiService from '../../services/api/api.service';
import { IApiServiceResponse } from '../../services/api/api.service.response';

import ItemCliente from '../../models/ItemCliente';
import ItemRegistroInvitacion from '../../models/ItemRegistroInvitacion';

import exclamationIcon from '../../assets/img16/info_16.png';

const textFieldStyles : ITextFieldStyles = {
    root: {},
    fieldGroup: {
        borderColor: 'var(--color-primario)',
        borderRadius: 5,
        selectors: {
            "&:hover, &:focus, &:active": {
                borderColor: 'var(--color-primario)',
                borderRadius: 5,
            },
        },
    },
    prefix: {},
    suffix: {},
    field: {},
    icon: {},
    description: {},
    wrapper: {},
    errorMessage: {},
    subComponentStyles: {
        label: {},
    },
    revealButton: {},
    revealSpan: {},
    revealIcon: {},
};

const modalStyles : IModalStyles = {
    root: {},
    main: {
        height: '80vh',
        width: '80vw',
        padding: 25,
    },
    scrollableContent: {
        height: '100%',
    },
    layer: {},
    keyboardMoveIconContainer: {},
    keyboardMoveIcon: {},
};

const onRenderTextFieldLabel : IRenderFunction<ITextFieldProps> = (props?: ITextFieldProps, defaultRender?: (props?: ITextFieldProps) => JSX.Element | null) : JSX.Element => {
    const content = (title: string, description: string) : JSX.Element => {
        return (
            <Stack>
                <StackItem>
                    <Text className='fw-bold'>
                        {title}
                    </Text>
                </StackItem>
                <StackItem>
                    <Text>
                        {description}
                    </Text>
                </StackItem>
            </Stack>
        );
    };

    return props && props.label ? (
        <Stack 
            horizontal={true}
            className="d-flex flex-row align-items-center py-2">
            <StackItem>
                <Text>
                    {props?.label}
                </Text>
            </StackItem>
            {
                props.data ? (
                    <StackItem>
                        <TooltipHost 
                            content={content(props.label, props.data)}
                            tooltipProps={{
                                calloutProps: {
                                    styles: {
                                        beak: { 
                                            background: '#F8F9FA',
                                        },
                                        beakCurtain: { 
                                            background: '#F8F9FA',
                                        },
                                        calloutMain: { 
                                            background: '#F8F9FA',
                                        },
                                    },
                                },
                              }}>
                            <Image 
                                className='ms-1'
                                height={16}
                                width={16}
                                src={exclamationIcon} 
                                alt="Información" />
                        </TooltipHost>
                    </StackItem>
                ) : (
                    null
                )
            }
        </Stack>
    ) : (
        <></>
    );
};

interface IRegisterInvitationPageProps {};

const RegisterInvitationPage = (props: IRegisterInvitationPageProps) => {
    const auth = useAuthStatus();
    
    const [invitationToken, setInvitationToken] = useState<string>();
    const [invitationError, setInvitationError] = useState<string>('');
    const [registerInvitation, setRegisterInvitation] = useState<ItemRegistroInvitacion>();
    const [customer, setCustomer] = useState<ItemCliente>(new ItemCliente());
    const [isUseTermsOpen, setIsUseTermsOpen] = useState<boolean>(false);
    const [isPrivacyPolicyOpen, setIsPrivacyPolicyOpen] = useState<boolean>(false); 
    const [registerError, setRegisterError] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [step, setStep] = useState<number>(1);
    const [success, setSuccess] = useState<string>('');

    const navigate = useNavigate();

    useEffect(() => {
        let queryParams : queryString.ParsedQuery<string> = queryString.parse(window.location.search);
        setInvitationToken(queryParams.ItemToken?.toString());
    }, []);

    useEffect(() => {
        if (invitationToken) {
            setLoading(true);
            ApiService.Token({
                Token: invitationToken,
            }).then((response: IApiServiceResponse<ItemRegistroInvitacion>) => {
                setRegisterInvitation(response.Datos);
                setStep(2);
                setLoading(false);
            }, (error: string) => {
                setInvitationError(error);
                setLoading(false);
            });
        }
    }, [invitationToken]);

    useEffect(() => {
        if (registerInvitation) {
            setCustomer((customer: ItemCliente) => {
                customer.Email = registerInvitation.IdUsuario;
                customer.Espacio = registerInvitation.Espacio;
                return customer;
            });
        }
    }, [registerInvitation]);

    const onChangePassword = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) : void => {
        setCustomer({ 
            ...customer,
            P1: newValue ? newValue : "",
            P2: newValue ? newValue : "",
        });
    };

    const onChangeName = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) : void => {
        setCustomer({ 
            ...customer, 
            Nombre: newValue ? newValue : "",
        });
    };

    const onKeyUp = (event: React.KeyboardEvent) : void => {
        if (event.key === "Enter") {
            onRegister();
        }
    };

    const onRegister = () : void => {
        if (registerInvitation) {
            setRegisterError('');
            if (customer.P1 === "" || customer.P2 === "" || customer.Nombre === "") {
                setRegisterError("Alguno de los campos introducidos no es válido.");
            }
            else {
                setLoading(true);
                auth.confirmRegisterInvitation(customer, registerInvitation.TokenVerificacion).then((response: string) => {
                    setSuccess(response);
                    setStep(3);
                    setLoading(false);
                }, (error: string) => {
                    setRegisterError(error);
                    setLoading(false);
                });
            }
        }
    };

    return (
        <>
            {
                (step === 1) ? (
                    <Stack>
                        <StackItem className='mb-3'>
                            <Text 
                                className='fw-bold'
                                variant='large'>
                                Registro por invitación:
                            </Text>
                        </StackItem>
                        <StackItem className='mb-3'>
                            <ErrorMessageComponent message={invitationError} />
                        </StackItem>
                        <StackItem className='mb-3'>
                            <ButtonPrimaryComponent 
                                className='text-uppercase'
                                text='Iniciar sesión'
                                onClick={() => navigate("/")} />
                        </StackItem>
                    </Stack>
                ) : (step === 2) ? (
                    <>
                        <Stack>
                            <StackItem className='mb-3'>
                                <Text 
                                    className='fw-bold'
                                    variant='large'>
                                    Registro por invitación:
                                </Text>
                            </StackItem>
                            <StackItem className='mb-3'>
                                <TextField
                                    type="email"
                                    autoComplete="off"
                                    maxLength={100}
                                    defaultValue={customer.Email}
                                    label="Correo electrónico"
                                    data="Indica tu dirección de email, como propietario del espacio. Esta dirección será tu identificador de usuario."
                                    onRenderLabel={onRenderTextFieldLabel}
                                    disabled={true}
                                    styles={textFieldStyles}
                                    onKeyUp={onKeyUp} />
                            </StackItem>
                            <StackItem className='mb-3'>
                                <TextField
                                    type="password"
                                    autoComplete="on"
                                    canRevealPassword={true}
                                    onChange={onChangePassword}
                                    defaultValue={customer.P1}
                                    label="Contraseña"
                                    data="Indica tu contraseña."
                                    onRenderLabel={onRenderTextFieldLabel}
                                    styles={textFieldStyles}
                                    onKeyUp={onKeyUp} />
                            </StackItem>
                            <StackItem className='mb-3'>
                                <TextField
                                    type="text"
                                    autoComplete="on"
                                    maxLength={100}
                                    onChange={onChangeName}
                                    defaultValue={customer.Nombre}
                                    label="Nombre"
                                    data="Indícanos tu nombre completo, para poder dirigirnos a ti como propietario del espacio."
                                    onRenderLabel={onRenderTextFieldLabel}
                                    styles={textFieldStyles}
                                    onKeyUp={onKeyUp} />
                            </StackItem>
                            <StackItem className='mb-3'>
                                <TextField
                                    type="text"
                                    autoComplete="off"
                                    maxLength={100}
                                    defaultValue={customer.Espacio}
                                    label="Espacio"
                                    data="Indica el nombre con el que se identificará al espacio de trabajo común para tu equipo y proyecto."
                                    onRenderLabel={onRenderTextFieldLabel}
                                    disabled={true}
                                    styles={textFieldStyles}
                                    onKeyUp={onKeyUp} />
                            </StackItem>
                            <StackItem className='mb-3'>
                                <Text>
                                    Al pulsar <b>REGISTRAR</b>, aceptas los <Link 
                                        to="#" 
                                        onClick={() => setIsUseTermsOpen(true)}
                                        style={{
                                            color: 'var(--color-primario)',
                                        }}>
                                        Términos de uso
                                    </Link> y las <Link 
                                        to="#" 
                                        onClick={() => setIsPrivacyPolicyOpen(true)}
                                        style={{
                                            color: 'var(--color-primario)',
                                        }}>
                                        Políticas de privacidad
                                    </Link> de MyMov
                                </Text>
                            </StackItem>
                            <StackItem className='mb-3'>
                                <Stack  
                                    horizontal={true}
                                    className="d-flex align-items-center justify-content-between">
                                    <StackItem>
                                        <ButtonPrimaryComponent 
                                            className='text-uppercase'
                                            text='Registrar'
                                            onClick={onRegister} />
                                    </StackItem>
                                    <StackItem className='text-center flex-fill align-items-center'>
                                        <Link 
                                            to="/"
                                            style={{
                                                color: 'var(--color-primario)',
                                                textDecoration: 'none',
                                            }}>
                                            ¿Ya tienes una cuenta? Iniciar sesión.
                                        </Link>
                                    </StackItem>
                                </Stack>
                            </StackItem>
                            <StackItem className='mb-3'>
                                <ErrorMessageComponent message={registerError} />
                            </StackItem>
                        </Stack>
                        <Modal
                            titleAriaId="modal-use-terms"
                            isOpen={isUseTermsOpen}
                            onDismiss={() => setIsUseTermsOpen(false)}
                            isBlocking={true}
                            styles={modalStyles}
                            firstFocusableSelector="top">
                            <Stack className='top'>
                                <StackItem className='p-3'>
                                    <TermsAndConditionsComponent />
                                </StackItem>
                                <StackItem className='text-center my-5'>
                                    <ButtonPrimaryComponent
                                        text='Aceptar'
                                        className='text-uppercase'
                                        onClick={() => setIsUseTermsOpen(false)} />
                                </StackItem>
                            </Stack>
                        </Modal>
                        <Modal
                            titleAriaId="modal-privacy-policy"
                            isOpen={isPrivacyPolicyOpen}
                            onDismiss={() => setIsPrivacyPolicyOpen(false)}
                            isBlocking={true}
                            styles={modalStyles}
                            firstFocusableSelector="top">
                            <Stack className='top'>
                                <StackItem className='p-3'>
                                    <PrivacyPolicyComponent />
                                </StackItem>
                                <StackItem className='text-center my-5'>
                                    <ButtonPrimaryComponent
                                        text='Aceptar'
                                        className='text-uppercase'
                                        onClick={() => setIsPrivacyPolicyOpen(false)} />
                                </StackItem>
                            </Stack>
                        </Modal>
                    </>
                ) : (step === 3) ? (
                    <Stack>
                        <StackItem className='mb-3'>
                            <Text 
                                className='fw-bold'
                                variant='large'>
                                Registro por invitación:
                            </Text>
                        </StackItem>
                        <StackItem className='mb-3'>
                            <SuccessMessageComponent message={success} />
                        </StackItem>
                        <StackItem className='mb-3'>
                            <ButtonPrimaryComponent 
                                className='text-uppercase'
                                text='Iniciar sesión'
                                onClick={() => navigate("/")} />
                        </StackItem>
                    </Stack>
                ) : (
                    null
                )
            }
            <LoaderComponent loading={loading} />
        </>
    );
};

export default RegisterInvitationPage;
